import { Calculator, ChartPie, ListChecks, Target, Lightbulb } from "@phosphor-icons/react";
import Budgeting from "@/pages/Budgeting";

function Card({ title, icon: Icon, accent = "amber", children }) {
  return (
    <div className={`rounded-sm border border-zinc-800 bg-zinc-900/40 p-5 hover:border-${accent}-500/30 transition-colors`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} weight="bold" className={`text-${accent}-400`} />
        <h3 className="font-heading text-sm font-bold text-zinc-100 tracking-tight">{title}</h3>
      </div>
      <div className="text-[12px] leading-relaxed text-zinc-400 space-y-2">{children}</div>
    </div>
  );
}

export default function Budgeting101() {
  return (
    <div className="px-5 py-6 space-y-8 max-w-[1200px]" data-testid="budgeting-101-page">
      {/* Hero */}
      <div className="rounded-sm border border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-zinc-900/40 to-zinc-950 p-6">
        <div className="flex items-center gap-3 mb-3">
          <Calculator size={28} weight="bold" className="text-amber-400" />
          <h2 className="font-heading text-2xl font-bold text-zinc-50 tracking-tight">Budgeting 101</h2>
        </div>
        <p className="text-[13px] text-zinc-400 leading-relaxed max-w-2xl">
          A budget is just a plan for what your money does next. The two most-used frameworks are the
          <span className="text-amber-300"> 50/30/20 rule</span> for simplicity and
          <span className="text-amber-300"> zero-based budgeting</span> for control. Pick one, run it for 90 days,
          then adapt.
        </p>
      </div>

      {/* 50/30/20 */}
      <Card title="The 50/30/20 Rule — fast, forgiving, beginner-friendly" icon={ChartPie} accent="emerald">
        <p>Take your <strong className="text-zinc-200">after-tax monthly income</strong> and split it three ways:</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 my-3">
          <div className="p-3 rounded-sm bg-emerald-500/5 border border-emerald-500/20">
            <p className="text-[10px] uppercase tracking-wider text-emerald-400 font-bold mb-1">50% · Needs</p>
            <p className="text-[11px] text-zinc-300">Rent/mortgage, utilities, groceries, transport, insurance, minimum debt payments. If your needs exceed 50%, the problem is income or housing — not coffee.</p>
          </div>
          <div className="p-3 rounded-sm bg-amber-500/5 border border-amber-500/20">
            <p className="text-[10px] uppercase tracking-wider text-amber-400 font-bold mb-1">30% · Wants</p>
            <p className="text-[11px] text-zinc-300">Dining out, streaming, hobbies, gym, travel, the upgrade-from-fine-to-nice category. Caps lifestyle creep before it caps your future.</p>
          </div>
          <div className="p-3 rounded-sm bg-blue-500/5 border border-blue-500/20">
            <p className="text-[10px] uppercase tracking-wider text-blue-400 font-bold mb-1">20% · Savings &amp; Debt</p>
            <p className="text-[11px] text-zinc-300">Emergency fund first, then high-interest debt payoff, then retirement and investing. Pay yourself before anyone else.</p>
          </div>
        </div>
        <p className="text-[11px] text-zinc-500 italic">When to use it: when you want guard-rails without spreadsheets. It's a posture, not a prison.</p>
      </Card>

      {/* Zero-based */}
      <Card title="Zero-Based Budgeting — every dollar gets a job" icon={ListChecks} accent="violet">
        <p>
          Income minus every assigned expense, save, and debt payment must equal <strong className="text-zinc-200">zero</strong>.
          You don't end the month with "leftover" money — every dollar was already told what to do.
        </p>
        <ol className="list-decimal pl-5 space-y-1.5 my-3 text-[12px]">
          <li>Write down your expected monthly income.</li>
          <li>List every category — fixed bills, groceries, gas, savings buckets, fun money.</li>
          <li>Assign every dollar to a category until income − allocations = $0.</li>
          <li>Track during the month. Move money between categories as life happens — but never spend money that wasn't allocated.</li>
        </ol>
        <p className="text-[11px] text-zinc-500 italic">When to use it: when you want maximum control, are paying off debt fast, or your income varies month to month.</p>
      </Card>

      {/* Pitfalls / pro tips */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Common pitfalls" icon={Target} accent="red">
          <ul className="list-disc pl-5 space-y-1.5">
            <li><strong className="text-zinc-200">Forgetting irregular bills</strong> — annual insurance, car registration, holidays. Divide each by 12 and stash monthly.</li>
            <li><strong className="text-zinc-200">No "fun money" line</strong> — ironclad budgets break the moment you breathe. Build slack in.</li>
            <li><strong className="text-zinc-200">Budgeting gross income</strong> — always work in take-home pay after taxes and benefits.</li>
            <li><strong className="text-zinc-200">Treating savings as leftover</strong> — savings is the FIRST line item, not the last.</li>
          </ul>
        </Card>
        <Card title="Pro tips" icon={Lightbulb} accent="amber">
          <ul className="list-disc pl-5 space-y-1.5">
            <li><strong className="text-zinc-200">Automate everything</strong> — payday → savings + bills + investments before you can touch them.</li>
            <li><strong className="text-zinc-200">Two accounts minimum</strong> — one for spending, one for bills/savings. Cash leaks where it can see itself.</li>
            <li><strong className="text-zinc-200">Track for 30 days first</strong> — you can't budget what you haven't measured.</li>
            <li><strong className="text-zinc-200">Review monthly, not daily</strong> — zoom out. Trends matter, single coffees don't.</li>
          </ul>
        </Card>
      </div>

      {/* Calculator */}
      <div className="pt-2">
        <div className="flex items-center gap-2 mb-3 px-1">
          <Calculator size={16} weight="bold" className="text-amber-400" />
          <h3 className="font-heading text-base font-bold text-zinc-100 tracking-tight">Budgeting Calculator &amp; Net-Worth Tracker</h3>
        </div>
        <p className="text-[11px] text-zinc-500 mb-4 px-1">
          Run the numbers. Save monthly snapshots to see your budget and net worth move over time.
        </p>
        <Budgeting />
      </div>
    </div>
  );
}
